import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import StatusBadge from "../../generals/StatusBadge";
import { departmentService } from "../../services/department.service";
import LogicContext from "../../Context/LogicContext";
import {
  SetDepartmentList,
} from "../../store/departmentAtom";
import DepartmentSideScroll from "./DepartmentSideScroll";
import DepartmentOverview from "./DepartmentOverview";
import DepartmentFormModal from "./DepartmentFormModal";
import "./DepartmentSetup.css";

function DepartmentDetailsPage() {
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [formMode, setFormMode] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [error, setError] = useState("");
  const latestRequest = useRef(0);
  const logicContext = useContext(LogicContext);
  const setDepartmentList = SetDepartmentList();
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3] || "departments";
  const basePath = `/${currModuleId}/${currModuleName}/${currUserMenu}`;

  useEffect(() => {
    document.title = "Department Details";
  }, []);

  useEffect(() => {
    if (!id) return;

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    const controller = new AbortController();

    setLoading(true);
    setError("");

    departmentService
      .getDepartmentDetails(id, { signal: controller.signal })
      .then((res) => {
        if (latestRequest.current !== requestId) return;
        setDepartment(res.department);
      })
      .catch((err) => {
        if (latestRequest.current !== requestId) return;
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setError("Unable to load department details. Please try again.");
        setDepartment(null);
      })
      .finally(() => {
        if (latestRequest.current === requestId) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [id]);

  function viewDepartmentDetails(departmentId) {
    navigate(`${basePath}/${departmentId}`);
  }

  async function refreshDepartmentList() {
    const result = await departmentService.getDepartments();
    setDepartmentList({
      departments: result.departments,
      raw: result.raw,
      loaded: true,
    });
  }

  async function refreshAfterWrite() {
    const [detailResult, listResult] = await Promise.all([
      departmentService.getDepartmentDetails(id),
      departmentService.getDepartments(),
    ]);
    setDepartment(detailResult.department);
    setDepartmentList({
      departments: listResult.departments,
      raw: listResult.raw,
      loaded: true,
    });
  }

  async function handleEditDepartment({ name, statusId }) {
    setActionLoading("edit");
    try {
      const result = await departmentService.updateDepartment(
        id,
        name,
        statusId
      );
      logicContext.showToast(result.message, "success");
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          refreshError?.message || "Unable to refresh department details.",
          "error"
        );
      }
      setFormMode("");
    } finally {
      setActionLoading("");
    }
  }

  async function handleCreateDepartment({ name }) {
    setActionLoading("create");
    try {
      const result = await departmentService.createDepartment(name);
      logicContext.showToast(result.message, "success");
      try {
        await refreshDepartmentList();
      } catch (refreshError) {
        logicContext.showToast(
          refreshError?.message || "Unable to refresh departments.",
          "error"
        );
      }
      setFormMode("");
    } finally {
      setActionLoading("");
    }
  }

  function handleStatusChange() {
    const action = department.isActive ? "deactivate" : "activate";
    setConfirmAction({
      action: "status",
      title: `Confirm ${action}`,
      message: `Are you sure you want to ${action} ${department.name}?`,
    });
  }

  async function applyStatusChange() {
    const nextStatusId = department.isActive ? 2 : 1;
    setActionLoading("status");
    try {
      const result = await departmentService.setDepartmentStatus(
        id,
        nextStatusId
      );
      logicContext.showToast(result.message, "success");
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          refreshError?.message || "Unable to refresh department details.",
          "error"
        );
      }
    } catch (statusError) {
      logicContext.showToast(
        statusError?.message || "Unable to update department status.",
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function handleDeleteDepartment() {
    setConfirmAction({
      action: "delete",
      title: "Confirm Delete",
      message: `${department.name} will be permanently deleted. Do you want to continue?`,
    });
  }

  async function applyDeleteDepartment() {
    setActionLoading("delete");
    try {
      const result = await departmentService.deleteDepartment(id);
      logicContext.showToast(result.message, "success");
      try {
        await refreshDepartmentList();
      } catch (refreshError) {
        logicContext.showToast(
          refreshError?.message || "Unable to refresh departments.",
          "error"
        );
      }
      navigate(basePath);
    } catch (deleteError) {
      logicContext.showToast(
        deleteError?.message || "Unable to delete department.",
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function confirmPendingAction() {
    const action = confirmAction?.action;
    setConfirmAction(null);
    if (action === "status") return applyStatusChange();
    if (action === "delete") return applyDeleteDepartment();
  }

  const actionsDisabled = loading || Boolean(actionLoading) || !department;

  return (
    <div className="@container">
      <div className="mt-2 flex flex-col @xs:flex-row @xs:items-center @xs:justify-end">
        <div className="mr-6 @xs:mt-2 w-fit flex flex-row items-center px-2.5 py-[2px] border-[1px] border-gray-200 bg-white rounded-full">
          <StatusBadge
            statusId={department?.isActive ? 1 : "I"}
            statusName={department?.statusLabel || "-"}
          />
        </div>

        <div className="mt-2 flex flex-row items-center">
          <Tooltip title="Delete">
            <button
              onClick={handleDeleteDepartment}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaTrashAlt className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Edit">
            <button
              onClick={() => setFormMode("edit")}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <MdEdit className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Add New">
            <button
              onClick={() => setFormMode("create")}
              disabled={actionsDisabled}
              className="px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaPlus className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="mt-2 flex justify-end">
        <button
          onClick={handleStatusChange}
          disabled={actionsDisabled}
          className="px-4 py-2 text-sm rounded-md bg-gray-200 text-gray-700 font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {department?.isActive ? "Deactivate" : "Activate"}
        </button>
      </div>

      <div className="mt-6 w-full flex flex-col @xl:flex-row">
        <div className="my-1 mx-1 w-full @xl:w-[210px] flex-shrink-0 bg-white">
          <DepartmentSideScroll
            viewDepartmentDetails={viewDepartmentDetails}
          />
        </div>

        <div className="mt-10 @xl:mt-0 w-full @xl:ml-4">
          <div className="pb-2 border-b-[1.5px] border-b-gray-200 @xl:px-4 flex flex-row flex-wrap @md:justify-between max-w-3xl">
            <div className="mt-2 mr-2 text-sm font-medium cursor-pointer text-primary">
              Overview
            </div>
          </div>

          <div className="mt-2 max-auto max-w-6xl text-filterTextColor">
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="department-skeleton h-8 w-1/2 rounded" />
                <div className="department-skeleton mt-4 h-5 rounded" />
                <div className="department-skeleton mt-3 h-5 rounded" />
                <div className="department-skeleton mt-3 h-5 rounded" />
              </div>
            ) : null}

            {!loading && error ? (
              <div className="rounded-lg border border-red-100 bg-red-50 p-5 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            {!loading && !error && !department ? (
              <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600">
                Department not found.
              </div>
            ) : null}

            {!loading && !error && department ? (
              <DepartmentOverview department={department} />
            ) : null}
          </div>
        </div>
      </div>

      {formMode ? (
        <DepartmentFormModal
          mode={formMode}
          initialValues={
            formMode === "edit"
              ? { name: department?.name, statusId: department?.statusId }
              : { name: "", statusId: 2 }
          }
          onSubmit={
            formMode === "edit"
              ? handleEditDepartment
              : handleCreateDepartment
          }
          onClose={() => setFormMode("")}
        />
      ) : null}

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        aria-labelledby="department-confirm-title"
      >
        <DialogTitle id="department-confirm-title">
          {confirmAction?.title}
        </DialogTitle>
        <DialogContent>{confirmAction?.message}</DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmAction?.action === "delete" ? "error" : "primary"}
            onClick={confirmPendingAction}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DepartmentDetailsPage;
